/**
 * WtdPendingVerificationTray — surfaces every Win-the-Day delegation
 * a crew member has signed off but that still needs the manager's
 * eyes. Shows before/after photos, assignee name, and Approve /
 * Send-Back buttons. Empty state hides completely so the card only
 * exists when there is real work waiting.
 *
 * Backend contract: `zoneSheet.win_the_day[]` rows carry
 * `verification_status` ∈ { "pending_verification", "needs_manager_check",
 * "verified", "" }. This tray only lists the first two.
 *
 * Approve  → PATCH /zone-sheets/{date} { win_the_day_updates:[{id, manager_verified:true}] }
 * Send-back → PATCH /zone-sheets/{date} { win_the_day_updates:[{id, send_back:true, manager_verified_note}] }
 *
 * Uses shared SSE `wow:notification` broadcast to trigger a refresh
 * on the parent Manager Dashboard when other managers act on the
 * same row.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, X, ArrowClockwise, Camera, ImageSquare } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function statusMeta(vs) {
  if (vs === "pending_verification") {
    return { label: "Photos attached · needs your OK", chip: "bg-sky-100 text-sky-800 border-sky-200" };
  }
  return { label: "Signed off · no photos · needs your OK", chip: "bg-amber-100 text-amber-800 border-amber-200" };
}

export default function WtdPendingVerificationTray({
  zoneSheet,
  date,
  storeId,
  actorId,
  crew,
  onRefresh,
}) {
  const [busyId, setBusyId] = useState("");
  const [sendBackId, setSendBackId] = useState("");
  const [sendBackNote, setSendBackNote] = useState("");
  const [zoomPhoto, setZoomPhoto] = useState("");

  const items = useMemo(() => {
    const rows = (zoneSheet?.win_the_day || []).filter((r) => {
      const vs = r?.verification_status || "";
      return vs === "pending_verification" || vs === "needs_manager_check";
    });
    return rows;
  }, [zoneSheet]);

  const nameOf = (crewId) => {
    if (!crewId) return "";
    const c = (crew || []).find((x) => x.id === crewId);
    return c?.first_name || "";
  };

  const patch = async (row, payload) => {
    setBusyId(row.id);
    try {
      await axios.patch(
        `${API}/zone-sheets/${date}`,
        { actor_id: actorId, win_the_day_updates: [{ id: row.id, ...payload }] },
        { params: { store_id: storeId } },
      );
      if (onRefresh) await onRefresh();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't update — try again.");
    } finally {
      setBusyId("");
      setSendBackId("");
      setSendBackNote("");
    }
  };

  const approve = (row) => {
    patch(row, { manager_verified: true }).then(() => {
      toast.success(`${row.label} — verified`);
    });
  };

  const sendBack = (row) => {
    if (!sendBackNote.trim()) {
      // eslint-disable-next-line no-alert
      alert("Add a quick note so the crew knows what to fix.");
      return;
    }
    patch(row, { send_back: true, sent_back_reason: sendBackNote.trim() }).then(() => {
      toast.success(`${row.label} — sent back for redo`);
    });
  };

  if (!items.length) return null;

  return (
    <div
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-sky-50 p-3 shadow-sm"
      data-testid="wtd-pending-verification-tray"
    >
      <div className="flex items-center gap-2 mb-2.5">
        <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white inline-flex items-center justify-center">
          <ShieldCheck weight="fill" className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-black text-slate-900">Win-the-Day · Pending Verification</p>
          <p className="text-[11px] text-slate-500">
            {items.length} {items.length === 1 ? "task" : "tasks"} waiting on your OK
          </p>
        </div>
        <span
          className="text-[10px] font-black uppercase tracking-wider text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full"
          data-testid="wtd-pending-count"
        >
          {items.length}
        </span>
      </div>

      <div className="space-y-2">
        {items.map((row) => {
          const meta = statusMeta(row.verification_status);
          const assignee = row.assigned_to_name || nameOf(row.assigned_to) || "Unassigned";
          const before = row.before_photo_url;
          const after = row.after_photo_url;
          const hasSendBack = sendBackId === row.id;
          const busy = busyId === row.id;
          return (
            <div
              key={row.id}
              className="rounded-xl bg-white border border-slate-200 p-2.5"
              data-testid={`wtd-pending-row-${row.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-black text-slate-900 break-words">
                    {row.label}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Signed off by <span className="font-bold">{assignee}</span>
                  </p>
                </div>
                <span
                  className={`text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full border whitespace-nowrap ${meta.chip}`}
                >
                  {row.verification_status === "pending_verification" ? "Ready" : "No Photos"}
                </span>
              </div>

              {(before || after) && (
                <div className="mt-2 flex items-center gap-2">
                  {before ? (
                    <button
                      type="button"
                      onClick={() => setZoomPhoto(before)}
                      className="relative"
                      data-testid={`wtd-pending-before-${row.id}`}
                    >
                      <img
                        src={before}
                        alt="Before"
                        className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                      />
                      <span className="absolute top-0.5 left-0.5 text-[8px] font-black uppercase bg-slate-900/80 text-white px-1 rounded">
                        Before
                      </span>
                    </button>
                  ) : null}
                  {after ? (
                    <button
                      type="button"
                      onClick={() => setZoomPhoto(after)}
                      className="relative"
                      data-testid={`wtd-pending-after-${row.id}`}
                    >
                      <img
                        src={after}
                        alt="After"
                        className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                      />
                      <span className="absolute top-0.5 left-0.5 text-[8px] font-black uppercase bg-emerald-600/90 text-white px-1 rounded">
                        After
                      </span>
                    </button>
                  ) : null}
                </div>
              )}

              {row.verification_status === "needs_manager_check" && !before && !after && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                  <ImageSquare weight="duotone" className="w-3.5 h-3.5" />
                  Crew signed off without attaching photos — verify in person or send back.
                </div>
              )}

              {hasSendBack ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    autoFocus
                    value={sendBackNote}
                    onChange={(e) => setSendBackNote(e.target.value.slice(0, 200))}
                    placeholder="Why does this need a redo? (e.g. section 3 still messy)"
                    rows={2}
                    className="w-full text-xs border border-rose-200 rounded-lg p-2 focus:border-rose-400 outline-none"
                    data-testid={`wtd-pending-note-${row.id}`}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setSendBackId(""); setSendBackNote(""); }}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-[11px] font-black"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => sendBack(row)}
                      disabled={busy || !sendBackNote.trim()}
                      className="flex-1 px-2.5 py-1 rounded-lg bg-rose-600 text-white text-[11px] font-black disabled:opacity-50 inline-flex items-center justify-center gap-1"
                      data-testid={`wtd-pending-send-back-confirm-${row.id}`}
                    >
                      <ArrowClockwise weight="bold" className="w-3 h-3" />
                      Send Back
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => approve(row)}
                    disabled={busy}
                    className="flex-1 px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-[11px] font-black hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
                    data-testid={`wtd-pending-approve-${row.id}`}
                  >
                    <ShieldCheck weight="bold" className="w-3.5 h-3.5" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSendBackId(row.id); setSendBackNote(""); }}
                    disabled={busy}
                    className="px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 text-[11px] font-black disabled:opacity-50 inline-flex items-center justify-center gap-1"
                    data-testid={`wtd-pending-send-back-${row.id}`}
                  >
                    <ArrowClockwise weight="bold" className="w-3 h-3" />
                    Send Back
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {zoomPhoto && (
        <div
          className="fixed inset-0 z-[160] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setZoomPhoto("")}
          data-testid="wtd-photo-zoom"
        >
          <button
            type="button"
            onClick={() => setZoomPhoto("")}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/95 inline-flex items-center justify-center"
            aria-label="Close photo"
          >
            <X weight="bold" className="w-5 h-5 text-slate-900" />
          </button>
          <img
            src={zoomPhoto}
            alt="Task proof"
            className="max-w-full max-h-full rounded-xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
